// Core
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { motion, useAnimation } from 'framer-motion';
import { Box, Image, Divider, Button, Grid, Text, Flex, Skeleton, useBreakpointValue, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import {
	plansBgTop,
	plansBgCard,
	plansBorderColor,
	plansIconBgColor,
	plansButton,
	plansItemSaleTextColor,
	getLinkLoyaltyMembership,
	getLinkPetrolMembership
} from 'services/Utils';
import placeholderImage from 'assets/img/empty-plan.png';
import { ReactComponent as PopularIcon } from 'assets/svg/popular.svg';
import particlesImage from 'assets/img/particles.png';
import { ReactComponent as CoffeeIcon } from 'assets/svg/petrol/coffee.svg';
import { ReactComponent as CoinsIcon } from 'assets/svg/petrol/coins.svg';
import { ReactComponent as EXPIcon } from 'assets/svg/petrol/exp.svg';
import { ReactComponent as GiveawayEntriesIcon } from 'assets/svg/petrol/giveaway-entries.svg';
import { ReactComponent as MiniEntriesIcon } from 'assets/svg/petrol/mini-entries.svg';
import { ReactComponent as DiscountIcon } from 'assets/svg/petrol/discount.svg';
import priceOffImage from 'assets/img/price-off.png';

// Components
import { StarsIcon, CheckIcon, CheckActiveIcon, EntryArrowIcon } from 'components/Icons/Icons';
import Card from 'components/Card/Card';
import PetrolInclusionsItem from './PetrolInclusionsItem';
import FullInclusionsItem from './FullInclusionsItem';
import TablePetrol from './TablePetrol';
import ButtonSelector from 'components/ButtonSelector/ButtonSelector';

const Membership = ({ setMembershipData, paymentType, setPaymentType, variant}) => {
	const featureColor = '#8794C2';

	const bgDividerDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #9EDFFF 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
	const bgDividerWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  	const bgDivider = useColorModeValue(bgDividerWhite, bgDividerDark);
	const planBgColor = useColorModeValue('rgba(9, 155, 255, 0.10)', '#0D234D');
	const textPeriodColor = useColorModeValue('#262D47','white');

	const [viewFullInclusions, setViewFullInclusions] = useState(true);
	const dividerColor = useColorModeValue('#C8D3FF', '#252d47');

	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

	const isPopularStyle = {
		borderRadius: '16px',
		background: '#099BFF',
		boxShadow: isDark ? '0px 1px 22.4px 0px rgba(9, 155, 255, 0.60)' : 'unset',
		padding: '49px 6px 6px 6px'
	};

	const [data, setData] = useState([]);
    const { isLoading } = useQuery({
        queryKey: ['membership'],
        queryFn: async () => {
            const response = await apiService.getMemberships();
            setData(uniqueByTypeAndPayment(response.data));
            setMembershipData(uniqueByTypeAndPayment(response.data));
            return true;
        },
        refetchOnWindowFocus: false
    });

    const uniqueByTypeAndPayment = (array) => {
	  const seen = new Set();

	  const filtered = array.filter(item => {
	    if (item.type === 'newbie') { return false; }

	    const key = `${item.type}-${item.paymentType}`;
	    if (seen.has(key)) { return false; }

	    seen.add(key);
	    return true;
	  });

	  return filtered.sort((a, b) => {
	    if (a.type === 'fuel' && b.type !== 'fuel') { return 1; }
	    if (a.type !== 'fuel' && b.type === 'fuel') { return -1; }
	    return 0;
	  });
	};

	const isMobile = useBreakpointValue({ base: true, lg: false });

	const getMonthlyPriceByType = (type) => {
	  const item = data.find(
	    (el) => el.type === type && el.paymentType === 'monthly'
	  );

	  return item ? item.price * 12 / 100 : null;
	};

    return (
    	<>
    		{ variant === 'benefits' && 
	    		<Box textAlign="center" mb={{base: '24px', sm: '32px'}}>
			    	<Flex gap="24px" m="16px 0 14px 0" w="100%" justifyContent="center">
			        	<StarsIcon display={{base: 'none', md: 'block'}} fontSize="42px" mt="9px" />
			        	<Box
			        		 borderRadius="16px"
			        		 p="6px 16px"
			        		 mt="-6px"
			  			     background="white">
			        		<Text variant="header-gradient" fontSize={{base: '42px', md: '56px', lg: '64px'}} pr="4px">Exclusive Membership</Text>
			        	</Box>
			        	<StarsIcon display={{base: 'none', md: 'block'}} fontSize="42px" mt="9px" />
			        </Flex>

			        <Text variant="header" fontSize={{base: '40px', md: '44px', lg: '64px'}}>Unlock Premium Benefits</Text>
		        </Box>
		    }

		    { variant === 'home' && 
	    		<Box textAlign="center" mb={{base: '24px', sm: '32px'}}>
			    	<Flex display={{base: 'none', md: 'flex'}} gap="12px" pl="26px" m="16px 0 14px 0" w="100%" justifyContent="center">
			        	<StarsIcon fontSize="32px" mt="9px" />
			        	<Box position="relative">
			        		<Text variant="header-gradient" fontSize="48px">CHOOSE YOUR</Text>
		        			<Divider mt="4px" h="2px" bg={bgDivider} position="absolute" border="unset !important" />
		        			{ isDark && <Box className="abstract-grad" mt="0px" /> }
			        	</Box> 
			        	<Box
			        		 borderRadius="16px"
			        		 p="6px 16px"
			        		 mt="-6px"
			  			     background="white">
			        		<Text variant="header-gradient" fontSize="48px">MEMBERSHIP</Text>
			        	</Box>
			        	<StarsIcon fontSize="32px" mt="9px" />
			        </Flex>

			        <Box display={{base: 'block', md: 'none'}} position="relative">
		        		<Text variant="header-gradient" fontSize="36px">CHOOSE YOUR</Text>
	        			<Divider mt="0px" h="2px" bg={bgDivider} position="absolute" border="unset !important" />
	        			{ isDark && <Box className="abstract-grad" mt="0px" /> }
			        </Box> 
			        <Flex alignItems="center" display={{base: 'flex', md: 'none'}} gap="12px" m="16px 0 14px 0" w="100%" justifyContent="center">
			        	<StarsIcon fontSize="24px" />
			        	<Box
			        		 borderRadius="16px"
			        		 p="6px 16px"
			  			     background="white">
			        		<Text variant="header-gradient" fontSize="36px">MEMBERSHIP</Text>
			        	</Box>
			        	<StarsIcon fontSize="24px" />
			        </Flex>

			        <Text variant="header" fontSize={{base: '24px', sm: '32px'}}>Select a package below & become a member today!</Text>
		        </Box>
		    }

	    	{ isLoading ? ( 
	    		<Box>
		            <Skeleton 
		            	borderRadius="16px"
		            	h="56px"
		            	m="0 auto"
		            	w={{base: '100%', sm: '361px'}} />
		        
			        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr', md: 'repeat(auto-fit, minmax(150px, 1fr))'}} 
			        	gap="12px" 
			        	rowGap="24px" 
			        	templateRows='1fr' 
			        	alignItems="stretch"
			        	mt={{base: '24px', sm: '16px'}} 
			        	mb={{ base: '24px', sm: '32px', lg: '48px'}}>
			        	<Skeleton style={{marginTop: !isMobile ? '24px' : '0px'}} borderRadius="16px" h="200px" />
			            <Skeleton style={{marginTop: !isMobile ? '24px' : '0px'}} borderRadius="16px" h="200px" />
			            <Skeleton style={{marginTop: !isMobile ? '24px' : '0px'}} borderRadius="16px" h="200px" />
			            <Skeleton style={{marginTop: !isMobile ? '24px' : '0px'}} borderRadius="16px" h="200px" />
			        </Grid>

			        <Skeleton 
		        		mt="24px"
		            	borderRadius="16px"
		            	h="200px" />

		            <Grid
			        	overflow="hidden"
			        	h={ viewFullInclusions ? '100%' : '0' }
			        	templateColumns={{ base: '1fr', sm: '1fr 1fr', md: 'repeat(auto-fit, minmax(150px, 1fr))'}} 
			        	gap="12px" 
			        	templateRows='1fr' 
			        	mt="44px"
			        	>
			        	<Skeleton borderRadius="16px" h="200px" />
			        	<Skeleton borderRadius="16px" h="200px" />
			        	<Skeleton borderRadius="16px" h="200px" />
			        	<Skeleton borderRadius="16px" h="200px" />
			        </Grid>
		        </Box>
	        ) : (<>
	        	<ButtonSelector
		            buttons={[
		              { key: 'yearly', name: 'Yearly', image: priceOffImage },
		              { key: 'monthly', name: 'Monthly' },
		            ]}
		            selected={paymentType}
		            onSelect={setPaymentType}
		            w={{base: '100%', sm: '361px'}}
		            m="0 auto"
		            />

		        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr', md: 'repeat(auto-fit, minmax(150px, 1fr))'}} 
		        	gap="12px" 
		        	rowGap="24px" 
		        	templateRows='1fr' 
		        	alignItems="stretch"
		        	mt={{base: '24px', sm: '16px'}} 
		        	mb={{ base: '24px', sm: '32px', lg: '48px'}}>
				    { data?.map((item, index) => (
				    	<>
				    		{item?.paymentType === paymentType && (() => {
							    const isPopular = item?.type === 'premium';

							    return (
							    	<Box
							    		style={{marginTop: !isMobile ? '24px' : '0px'}}
							    		position="relative"
							    		overflow="visible"
							    	>
							    		{ isPopular && (
							    			<Text 
							    				w="163px"
							    				borderRadius="100px"
												position="absolute" 
							    				variant="header" 
							    				padding="2px 8px"
							    				fontSize="16px !important"
							    				fontWeight="800 !important" 
							    				zIndex="999"
							    				left="50%"
							    				fontStyle="normal"
    											transform="translate(-50%, -50%)"
    											bg={`url(${particlesImage})`}
									        	backgroundPosition="center"
									        	backgroundColor="#874DE3"
									        	backgroundSize="40%"
							    				color="white"
									        	overflow="hidden"
									        	_before={{
									        		content: '""',
												    position: 'absolute',
												    width: '80%',
												    height: '100%',
												    background: 'rgb(255 255 255 / 75%)',
												    filter: 'blur(11px)',
												    top: '-21px',
												    borderRadius: '100%',
												    left: '-31px'
									        	}}
							    				>
							    				<Flex gap="4px">
							    					<PopularIcon />
						    						Most Popular
						    					</Flex>
							    			</Text>
							    		)}
							    		
								        <Card zIndex="2" 
								        	borderRadius="12px"
											border={`3px solid ${plansBorderColor(item?.type)}`}
								        	padding="0 0 16px 0" 
								        	key={index} 
								        	overflow="hidden" 
								        	background={plansBgCard(item?.type, isDark)}
								        	boxShadow={ isDark ? '0 1px 16px 6px rgba(188, 186, 184, 0.25), 0 2px 6px 0 rgba(188, 186, 184, 0.08)' : 'none'}
								        	position="relative">
								        	<Image src={plansBgTop(item?.type)} w="100%" h="85px" alt={item?.name} position="absolute" />

								        	<Box p="16px 12px" zIndex="3" textAlign="center">								        		
							        			<Box 
							        				bg={plansIconBgColor(item?.type, isDark)} 
							        				borderRadius="12px" 
							        				w="50px" h="50px" p="6px"
							        				border={`2px solid ${plansBorderColor(item?.type)}`}
							        				m="14px auto 8px auto">
							        				<Image 
							        					src={item?.image ?? placeholderImage} 
							        					w="34px" h="34px" maxW="34px" alt={item?.name} />
							        			</Box>
							        			<Text 
							        				mt="6px" 
							        				variant="header" 
							        				textTransform="capitalize" 
							        				fontStyle="normal" 
							        				p={{base: '0 20px', md: '0 60px'}}
							        				fontSize="20px">{item?.type === 'fuel' ? 'Petrol Saver' : item?.type } Membership</Text>

							        			{ item?.type !== 'fuel' && item?.paymentType === 'yearly' ?
							        				<Text
							        					variant="title-lg"
							        					fontSize="18px"
							        					h="20px"
							        					m={{ base: '8px 0 0px 0', sm: '8px 0 -12px 0'}}
							        					sx={{
							        						'letter-spacing': '-0.4px',
															'text-decoration-line': 'line-through',
															'background-clip': 'text',
															'-webkit-background-clip': 'text',
															'-webkit-text-fill-color': 'transparent'
							        					}}
							        					bg={plansItemSaleTextColor(item?.type)}
							        					>
							        					{getMonthlyPriceByType(item?.type)}
							        				</Text>
							        				: <Box h="20px" m="8px 0 -12px 0" />
							        			}

								        		<Flex gap="2px" justifyContent="center" alignItems="flex-end" 
								        			mt={{base: '6px', sm: '12px'}} 
								        			mb={{base: '8px', sm: '18px'}}>
												    <Text variant="header" mb="-4px" fontSize={{base: '24px', sm: '32px'}}>${item?.price / 100}</Text>
												    <Text fontSize={{base: '16px', sm: '20px'}} fontWeight="800" color={textPeriodColor}>
												    	{ paymentType === 'monthly' ? '/month' : '/year'}
												    </Text>
												</Flex>

												<a href={
													item?.type === 'fuel' ? getLinkPetrolMembership() : getLinkLoyaltyMembership()
												} target="_blank" rel="noopener noreferrer">
													{ item?.type !== 'premium' ?
												        <Button 
												        	variant="bg-lg"
												        	bg={`url(${plansButton(item?.type)})`}
												        	backgroundPosition="center"
	    													backgroundSize="200%"
												        	>Buy now</Button>
												        : 
												        <Button 
												        	variant="bg-lg"
												        	bg={`url(${particlesImage})`}
												        	backgroundPosition="center"
												        	border="1px solid #A47CF3"
												        	backgroundColor="#874DE3"
												        	backgroundSize="40%"
												        	position="relative"
												        	overflow="hidden"
												        	_before={{
												        		content: '""',
															    position: 'absolute',
															    width: '40%',
															    height: '100%',
															    background: 'rgba(164, 124, 243, 0.75)',
															    filter: 'blur(11px)',
															    bottom: '-40px',
															    borderRadius: '100%'
												        	}}
												        	>Buy now</Button>
											        
													}
											    </a>
								        	</Box>

								        	<Box p={{base: '4px 14px 0px 14px', sm: '10px 14px 0px 14px'}} zIndex="3">
								        		<PetrolInclusionsItem type={item?.type} isDark={isDark} 
									        			icon={<DiscountIcon/>}
									        			title={'Fuel discounts*'} description={''} />
								        		{ item?.type !== 'fuel' && <>
									        		<PetrolInclusionsItem type={item?.type} isDark={isDark} 
									        			icon={<CoffeeIcon/>}
									        			title={`${item?.coffee ?? 0}th Coffee`} description={'Free'} />
									        		<PetrolInclusionsItem type={item?.type} isDark={isDark} 
									        			icon={<CoinsIcon/>}
									        			iconP="2px"
									        			title={`${item?.coins ?? 0} ${ item?.coins > 1 ? 'Coins' : 'Coin'}`} description="Per 50 L" />
									        		<PetrolInclusionsItem type={item?.type} isDark={isDark} 
									        			icon={<MiniEntriesIcon/>}
									        			title={`${item?.entriesMiniDraw ?? 0} ${ item?.entriesMiniDraw > 1 ? 'Mini Entries' : 'Mini Entry'}`} description="Per 50 L" />
									        		<PetrolInclusionsItem type={item?.type} isDark={isDark} 
									        			icon={<EXPIcon/>}
									        			title={`${item?.exp ?? 0} EXP`} description="Per 50 L" />
									        		<PetrolInclusionsItem type={item?.type} isDark={isDark} 
									        			icon={<GiveawayEntriesIcon/>}
									        			title={`${item?.entriesLoyalty ?? 0} ${ item?.entriesLoyalty > 1 ? 'Giveaways' : 'Giveaway'}`} description={`Auto Entry Per ${item?.type === 'fuel' ? '100' : '50' } L`} />
									        		</>
									        	}

								        		{ item?.type === 'fuel' && 
								        			<Box h={{ base: '0', lg: '159px'}} />
								        		}

								        		<Text m="14px 0 4px 7px" letterSpacing="-0.4px" variant="text-sm" fontSize="13px">* - depend on the station and location</Text>
								        	</Box>
								        </Card>
							        </Box>
					        	);
							})()}
				        </>
				    ))}	
		        </Grid>

		        { data?.length > 0 && 
			        <TablePetrol data={data} isDark={isDark} paymentType={paymentType} />
			    }

		        <Flex gap="8px" alignItems="center" mt={{base: '24px', md: '50px'}}>
		        	<Divider position="relative" zIndex="9" borderColor="unset" borderBottomColor={dividerColor} borderBottomWidth="1px" />
		        	<Flex gap="5px" pt="2px" cursor="pointer"
		        		onClick={
		        			() => setViewFullInclusions(!viewFullInclusions)
		        		}
		        		>
		        		<Text color="#099BFF" whiteSpace="nowrap" fontWeight="500">View Full Inclusions</Text>
		        		<EntryArrowIcon fontSize="24px" mt="-3px" 
		        			transform={ viewFullInclusions ? 'rotate(0deg)' : 'rotate(180deg)' }
		        		/>
		        	</Flex>
		        	<Divider position="relative" zIndex="9" borderColor="unset" borderBottomColor={dividerColor} borderBottomWidth="1px" />
		        </Flex>

		        <Grid
		        	overflow="hidden"
		        	h={ viewFullInclusions ? '100%' : '0' }
		        	templateColumns={{ base: '1fr', sm: '1fr 1fr', md: 'repeat(auto-fit, minmax(150px, 1fr))'}} 
		        	gap="12px" 
		        	templateRows='1fr' 
		        	mt="44px"
		        	>
		        	{ data?.map((item, index) => (
				    	<>
				    	{item?.paymentType === paymentType && (() => {
						return (
		        		<Box key={index} position="relative">
		        			<Flex gap="8px" alignItems="center" mb="10px">
			        			<Box w="20px" h="20px">
			        				<Image src={item?.image ?? placeholderImage} alt={item?.name} 
			        				borderRadius="4px" 
			        				w="20px" 
			        				h="20px" 
			        				maxW="20px"
			        				objectFit="cover" />
			        			</Box>
			        			<Text 
			        				variant="title" 
			        				fontSize="18px" 
			        				textTransform="capitalize" 
			        				fontWeight="800 !important">
			        				{item?.type === 'fuel' ? 'Petrol Saver' : item?.type } Membership
			        			</Text>
			        		</Flex>
			        			{ item?.type !== 'fuel' &&
			        				<>
									<FullInclusionsItem 
									  isPopular={true}
									  title={`${item?.entries || 0} FREE`} 
									  description={`
										  ${item.entries === 1 ? 'Entry' : 'Entries'} per every giveaway`}
									/>
									<FullInclusionsItem isPopular={true} title={`${item?.matesRates}% Access`} description="to Mates Rates Discounts" />
									<FullInclusionsItem isPopular={true} title={`"${item?.name}"`} description="Discounts with Selected Partners" />
									<FullInclusionsItem isPopular={true} title={`${item?.merchDiscount}%`} description="Off All LMCT+ Merchandise" />
									<FullInclusionsItem isPopular={true} title={
										item?.vipInvites === 'none' ? '' : item?.vipInvites === 'vip' ? 'VIP' : 'VIP+'
									} description={'Invites to LMCT+ Events and Games Across Australia'} />
									<FullInclusionsItem isPopular={true} title="" description="LMCT+ Early Bird Promotion Emails" />
									{ item?.freeHat && (
										<FullInclusionsItem isPopular={true} title="Free" description="LMCT+ Hat!" />
									)}

				        			<FullInclusionsItem type={item?.type} isDark={isDark} 
					        			title={`${item?.coffee ?? 0}th Coffee`} description={'Free'} />
					        		<FullInclusionsItem type={item?.type} isDark={isDark} 
					        			title={`${item?.coins ?? 0} ${ item?.coins > 1 ? 'Coins' : 'Coin'}`} description="Per 50 L" />
					        		<FullInclusionsItem type={item?.type} isDark={isDark} 
					        			title={`${item?.entriesMiniDraw ?? 0} ${ item?.entriesMiniDraw > 1 ? 'Mini Entries' : 'Mini Entry'}`} description="Per 50 L" />
					        		<FullInclusionsItem type={item?.type} isDark={isDark} 
					        			title={`${item?.exp ?? 0} EXP`} description="Per 50 L" />
					        		</>
					        	}
					        	<FullInclusionsItem type={item?.type} isDark={isDark} 
				        			title={'Fuel discounts*'} description={''} /> 

				        		{ item?.type !== 'fuel' && 
				        			<FullInclusionsItem type={item?.type} isDark={isDark} 
				        			title={`${item?.entriesLoyalty ?? 0} ${ item?.entriesLoyalty > 1 ? 'Giveaways' : 'Giveaway'}`} description={`Auto Entry Per ${item?.type === 'fuel' ? '100' : '50' } L`} />
				        		} 		
				    	</Box>
		        		);
						})()}
				        </>
				    ))}	
				</Grid>
				<Text 
					display={ viewFullInclusions ? 'block' : 'none' } 
					textAlign="center"
					w="100%"
					m="24px 0 46px 0"
					variant="text-md">
					* - depend on the station and location
				</Text>
				</>
		    )}
	    </>
    );
};


export default Membership;